import { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';

import PublishProject from '@/connection/projects/publish-project';
import UpdatePublishedUrl from '@/connection/projects/update-published-url';
import { RootState } from '@/store/main';
import { EProjectStatus } from '@/types/projects.types';
import toastTrigger from '@ui/toast/toast-trigger';
import updateRegistrationEmail from './update-registration-email';
import { getDefaultLanguage } from '@/utils/get-default-language';
import sleep from '@/utils/sleep';
import updateScheduledEmails from './update-scheduled-emails';
import { updateMostRecentActivity } from '..';
import { EToastTypes } from '@ui/toast/toast.types';

type IPublishWorkingProjectReducerHelperParams = {
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>;
  getState: () => unknown;
  publishedUrl?: string;
};

const publishWorkingProjectReducerHelper = async ({
  dispatch,
  getState: _getState,
  publishedUrl: _publishedUrl,
}: IPublishWorkingProjectReducerHelperParams) => {
  const getState = _getState as () => RootState;
  const rootState = getState();
  const _workingProject = rootState.project.workingProject;
  const currentChannel = rootState?.channels?.channels?.find(
    (channel) => channel.channel === rootState.user.user?.active_channel
  );
  const user = rootState.user.user;
  const publishedUrl = _publishedUrl || rootState.project.projectPublishedStatus?.url;
  const language = getDefaultLanguage(rootState.project.workingProject);
  if (!user) {
    throw new Error('No user found');
  }
  if (!_workingProject) {
    throw new Error('No working project found');
  }
  if (!publishedUrl) {
    throw new Error('No published URL found');
  }

  const workingProject = { ..._workingProject };
  workingProject.status = EProjectStatus.PUBLIC;

  const scheduledEmails = rootState.project.scheduledEmails;

  if (!currentChannel) {
    throw new Error('No current channel found');
  }

  try {
    await updateRegistrationEmail({
      workingProject,
      currentChannel,
      publishedUrl,
      user,
      dispatch,
    });
  } catch (e) {
    console.error(e);
    toastTrigger('Error updating registration email', {
      type: EToastTypes.error,
      autoClose: 5000,
      description: 'Please try again',
    });
  }

  let showEmailErrorToastAfterPublish = false;

  const recursiveWaitForEmailSaves = async (saveId: string, attempts = 0): Promise<void> => {
    const saveInProgress = getState().project?.saveId === saveId;
    if (saveInProgress) {
      if (attempts < 200) {
        await sleep(100); // wait a little
        return recursiveWaitForEmailSaves(saveId, attempts + 1);
      } else {
        if (workingProject?.registration_on) {
          showEmailErrorToastAfterPublish = true;
        }
        console.error(`Timed out waiting for email saves with ID: ${saveId}`);
      }
    }
  };
  const regEmailSAveId = 'publish-update-registration-email';
  // this ensures we have saved the working project correctly for emails before we publish so we don't
  // publish an event with stale email data (see saveWorkingProjectByFields inside update-registration-email.ts)
  await recursiveWaitForEmailSaves(regEmailSAveId);

  const publishResult = await PublishProject({ projectUuid: workingProject.uuid, url: publishedUrl });
  if ('error' in publishResult) {
    throw new Error(`Failed to publish project: ${publishResult.error}`);
  }

  dispatch(updateMostRecentActivity({ isPublish: true }));

  await UpdatePublishedUrl({ projectUuid: workingProject.uuid, newUrl: publishedUrl });

  // any unsent scheduled emails will need to be updated to account for any relevant event/session content changes
  // and to save changes made to the emails in the editor
  await updateScheduledEmails({
    currentChannel,
    scheduledEmails,
    workingProject,
    publishedUrl,
    language,
    dispatch,
  });

  if (showEmailErrorToastAfterPublish) {
    toastTrigger('Project published, however registration email may not have correctly updated', {
      type: EToastTypes.error,
      autoClose: 5000,
      closeOnClick: true,
      description: 'Please try publishing again',
    });
  }

  // ASSISTANT CHECKLIST ITEM: complete users checklist item for publishing an event
  // if (!user.onboarded.includes(ChecklistTitles.publishEvent))
  //   dispatch(addChecklistItem(token, user.id, user.onboarded, ChecklistTitles.publishEvent));

  // // if skip warning checked, updated user profile
  // if (skipWarningChecked && !user.onboarded.includes(skipAssistantWarning))
  //   dispatch(addChecklistItem(token, user.id, user.onboarded, skipAssistantWarning));
};

export default publishWorkingProjectReducerHelper;
